import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Hash, Loader2, Pencil, Plus, Trash2, UploadCloud } from "lucide-react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { api, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

function formatPhone(e164) {
  if (!e164) return "—";
  const p = parsePhoneNumberFromString(e164);
  return p ? p.formatInternational() : e164;
}

const blankNumber = {
  number: "",
  label: "",
  trunk_id: "",
  inbound_agent_id: "__none__",
  use_for_outbound: false,
  enabled: true,
};

export default function Numbers() {
  const [numbers, setNumbers] = useState([]);
  const [trunks, setTrunks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [filterTrunk, setFilterTrunk] = useState("__all__");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterOutbound, setFilterOutbound] = useState(false);

  const trunkById = useMemo(
    () => Object.fromEntries(trunks.map((t) => [t.id, t])),
    [trunks]
  );
  const agentById = useMemo(
    () => Object.fromEntries(agents.map((a) => [a.id, a])),
    [agents]
  );

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTrunk !== "__all__") params.trunk_id = filterTrunk;
      if (filterSearch) params.search = filterSearch;
      if (filterOutbound) params.use_for_outbound = true;
      const [n, t, a] = await Promise.all([
        api.get("/did-numbers", { params }),
        api.get("/trunks"),
        api.get("/agents"),
      ]);
      setNumbers(n.data);
      setTrunks(t.data);
      setAgents(a.data);
    } catch (err) {
      toastError(err, "Failed to load numbers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTrunk, filterSearch, filterOutbound]);

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/did-numbers/${confirmDelete.id}`);
      toast.success(`Deleted ${formatPhone(confirmDelete.number)}`);
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toastError(err, "Failed to delete");
    }
  };

  return (
    <div className="space-y-8" data-testid="numbers-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Phone Numbers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            DIDs you own, mapped to trunks and (optionally) inbound agents.
            Numbers tagged for outbound become caller-IDs available to campaigns.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setBulkOpen(true)}
            data-testid="numbers-bulk-btn"
          >
            <UploadCloud className="h-4 w-4" /> Bulk Import
          </Button>
          <Button
            onClick={() => setEditing({ ...blankNumber })}
            data-testid="numbers-add-btn"
          >
            <Plus className="h-4 w-4" /> Add Number
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-card p-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Trunk</Label>
          <Select value={filterTrunk} onValueChange={setFilterTrunk}>
            <SelectTrigger className="w-44" data-testid="numbers-filter-trunk">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All trunks</SelectItem>
              {trunks.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          placeholder="Search number or label…"
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          className="max-w-xs"
          data-testid="numbers-filter-search"
        />
        <div className="flex items-center gap-2">
          <Switch
            checked={filterOutbound}
            onCheckedChange={setFilterOutbound}
            data-testid="numbers-filter-outbound"
          />
          <Label className="cursor-pointer text-sm">Outbound only</Label>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : numbers.length === 0 ? (
          <Empty onAdd={() => setEditing({ ...blankNumber })} />
        ) : (
          <Table data-testid="numbers-table">
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Trunk</TableHead>
                <TableHead>Inbound Agent</TableHead>
                <TableHead>Outbound</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {numbers.map((n) => (
                <TableRow key={n.id} data-testid={`numbers-row-${n.id}`}>
                  <TableCell className="font-mono text-sm">{formatPhone(n.number)}</TableCell>
                  <TableCell className="text-sm">{n.label || "—"}</TableCell>
                  <TableCell>{trunkById[n.trunk_id]?.name || <span className="text-destructive text-xs">unknown</span>}</TableCell>
                  <TableCell>
                    {n.inbound_agent_id ? agentById[n.inbound_agent_id]?.name || <span className="text-destructive text-xs">unknown</span> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{n.use_for_outbound ? <Badge>Yes</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell>
                    {n.enabled ? <Badge>Enabled</Badge> : <Badge variant="secondary">Disabled</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing({ ...n, inbound_agent_id: n.inbound_agent_id || "__none__", label: n.label || "" })}
                      data-testid={`numbers-edit-${n.id}`}
                    >
                      <Pencil className="h-4 w-4" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmDelete(n)}
                      data-testid={`numbers-delete-${n.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" /> Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <NumberFormDialog
        editing={editing}
        trunks={trunks}
        agents={agents}
        onClose={() => setEditing(null)}
        onSaved={fetchAll}
      />
      <BulkImportDialog
        open={bulkOpen}
        trunks={trunks}
        agents={agents}
        onClose={() => setBulkOpen(false)}
        onDone={fetchAll}
      />
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="numbers-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete number?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes <b>{formatPhone(confirmDelete?.number)}</b> from inventory.
              Inbound calls to this number will fail and any campaigns using it as
              caller-ID will lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              data-testid="numbers-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Empty({ onAdd }) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="numbers-empty">
      <div className="rounded-full bg-secondary p-3"><Hash className="h-6 w-6 text-muted-foreground" /></div>
      <div className="text-base font-medium">No numbers yet</div>
      <p className="max-w-sm text-sm text-muted-foreground">
        Add the DIDs your carrier has provisioned for you. Each number must be tied to a trunk and can optionally be mapped to an inbound agent.
      </p>
      <Button onClick={onAdd} className="mt-2" data-testid="numbers-empty-add">
        <Plus className="h-4 w-4" /> Add Number
      </Button>
    </div>
  );
}

function NumberFormDialog({ editing, trunks, agents, onClose, onSaved }) {
  const isEdit = !!editing?.id;
  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    defaultValues: editing || blankNumber,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editing) reset(editing);
  }, [editing, reset]);

  if (!editing) return null;

  const onSubmit = async (values) => {
    const payload = {
      number: values.number,
      label: values.label || null,
      trunk_id: values.trunk_id,
      inbound_agent_id:
        values.inbound_agent_id && values.inbound_agent_id !== "__none__"
          ? values.inbound_agent_id
          : null,
      use_for_outbound: !!values.use_for_outbound,
      enabled: !!values.enabled,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/did-numbers/${editing.id}`, payload);
        toast.success("Number updated");
      } else {
        await api.post("/did-numbers", payload);
        toast.success("Number added");
      }
      onClose();
      onSaved();
    } catch (err) {
      toastError(err, "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="numbers-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit number" : "Add number"}</DialogTitle>
          <DialogDescription>
            Number must be in E.164 form (e.g. <span className="font-mono">+12025550123</span>).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="number">Number</Label>
            <Input
              id="number"
              placeholder="+12025550123"
              data-testid="numbers-input-number"
              {...register("number", { required: "Number is required" })}
            />
            {errors.number && <p className="text-xs text-destructive">{errors.number.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="label">Label (optional)</Label>
            <Input
              id="label"
              placeholder="NYC main line"
              data-testid="numbers-input-label"
              {...register("label", { maxLength: 80 })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Trunk</Label>
            <Select value={watch("trunk_id")} onValueChange={(v) => setValue("trunk_id", v)}>
              <SelectTrigger data-testid="numbers-select-trunk">
                <SelectValue placeholder="Select a trunk" />
              </SelectTrigger>
              <SelectContent>
                {trunks.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Inbound agent</Label>
            <Select value={watch("inbound_agent_id")} onValueChange={(v) => setValue("inbound_agent_id", v)}>
              <SelectTrigger data-testid="numbers-select-agent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None (no inbound routing)</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} <span className="text-xs text-muted-foreground">· {a.language}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div className="flex items-center gap-3">
              <Switch
                checked={!!watch("use_for_outbound")}
                onCheckedChange={(v) => setValue("use_for_outbound", v)}
                data-testid="numbers-toggle-outbound"
              />
              <Label className="cursor-pointer">Use for outbound (caller-ID)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={!!watch("enabled")}
                onCheckedChange={(v) => setValue("enabled", v)}
                data-testid="numbers-toggle-enabled"
              />
              <Label className="cursor-pointer">Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="numbers-submit">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Add number"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BulkImportDialog({ open, trunks, agents, onClose, onDone }) {
  const [tab, setTab] = useState("paste");
  const [textValue, setTextValue] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [trunkId, setTrunkId] = useState("");
  const [agentId, setAgentId] = useState("__none__");
  const [outbound, setOutbound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setTab("paste");
      setTextValue("");
      setCsvFile(null);
      setDragOver(false);
      setTrunkId("");
      setAgentId("__none__");
      setOutbound(false);
      setResult(null);
    }
  }, [open]);

  const submitText = async () => {
    const numbers = textValue
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (numbers.length === 0) {
      toast.error("Please paste at least one number");
      return;
    }
    if (!trunkId) {
      toast.error("Please pick a trunk");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/did-numbers/bulk-import", {
        numbers,
        trunk_id: trunkId,
        inbound_agent_id: agentId !== "__none__" ? agentId : null,
        use_for_outbound: outbound,
      });
      setResult(res.data);
      toast.success(`Imported ${res.data.created}`);
      onDone();
    } catch (err) {
      toastError(err, "Bulk import failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitCsv = async () => {
    if (!csvFile) {
      toast.error("Drop or pick a CSV file first");
      return;
    }
    if (!trunkId) {
      toast.error("Please pick a trunk");
      return;
    }
    if (csvFile.size > 10 * 1024 * 1024) {
      toast.error("CSV is larger than 10 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", csvFile);
    fd.append("trunk_id", trunkId);
    if (agentId !== "__none__") fd.append("inbound_agent_id", agentId);
    fd.append("use_for_outbound", outbound ? "true" : "false");
    setSubmitting(true);
    try {
      const res = await api.post("/did-numbers/bulk-import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(`Imported ${res.data.created}`);
      onDone();
    } catch (err) {
      toastError(err, "CSV import failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setCsvFile(f);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="numbers-bulk-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Bulk import numbers</DialogTitle>
          <DialogDescription>
            Paste numbers or upload a CSV. Duplicates and invalid entries are reported back.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} data-testid="numbers-bulk-tabs">
          <TabsList className="w-full">
            <TabsTrigger value="paste" className="flex-1" data-testid="numbers-bulk-tab-paste">
              Paste
            </TabsTrigger>
            <TabsTrigger value="csv" className="flex-1" data-testid="numbers-bulk-tab-csv">
              Upload CSV
            </TabsTrigger>
          </TabsList>
          <TabsContent value="paste" className="space-y-4 pt-3">
            <Textarea
              rows={6}
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              placeholder={"+12025550123\n+442071838750\n+39021234567"}
              data-testid="numbers-bulk-textarea"
            />
          </TabsContent>
          <TabsContent value="csv" className="space-y-4 pt-3">
            <label
              htmlFor="numbers-bulk-csv-input"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={
                "block cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors " +
                (dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")
              }
              data-testid="numbers-bulk-csv-drop"
            >
              <UploadCloud className="mx-auto h-8 w-8 text-muted-foreground" />
              <div className="mt-2 text-sm">
                {csvFile ? (
                  <span data-testid="numbers-bulk-csv-filename">
                    <b>{csvFile.name}</b> · {(csvFile.size / 1024).toFixed(1)} KB
                  </span>
                ) : (
                  <>Drag a CSV here, or <span className="text-primary underline">click to pick a file</span></>
                )}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Required column: <code>phone</code>. Optional: <code>label</code>. Max 10 MB.
              </div>
              <input
                id="numbers-bulk-csv-input"
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                data-testid="numbers-bulk-csv-input"
              />
            </label>
          </TabsContent>
        </Tabs>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Trunk</Label>
            <Select value={trunkId} onValueChange={setTrunkId}>
              <SelectTrigger data-testid="numbers-bulk-trunk"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {trunks.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Inbound agent</Label>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger data-testid="numbers-bulk-agent"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {agents.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={outbound} onCheckedChange={setOutbound} data-testid="numbers-bulk-outbound" />
          <Label className="cursor-pointer">Use for outbound</Label>
        </div>
        {result && (
          <div className="rounded-md border border-border p-3 text-xs space-y-2" data-testid="numbers-bulk-result">
            <div>Created: <b>{result.created}</b> · Skipped duplicates: <b>{result.skipped_duplicates.length}</b> · Invalid: <b>{result.invalid.length}</b></div>
            {result.invalid.length > 0 && (
              <ul className="text-destructive space-y-0.5 max-h-32 overflow-auto">
                {result.invalid.slice(0, 20).map((row, i) => (
                  <li key={i} className="font-mono">{row.input} — {row.reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{result ? "Close" : "Cancel"}</Button>
          {!result && (
            <Button
              onClick={tab === "paste" ? submitText : submitCsv}
              disabled={submitting}
              data-testid="numbers-bulk-submit"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Import
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
